import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import type { CellAlignment, CellEmphasis, LinkBehavior } from "../DataTable.types";

/**
 * Get alignment classes for cell content
 */
export function getCellAlignmentClasses(align: CellAlignment = "left"): string {
  switch (align) {
    case "center":
      return "justify-center text-center";
    case "right":
      return "justify-end text-right";
    default:
      return "justify-start text-left";
  }
}

/**
 * Get text styling based on emphasis level
 */
export function getCellTextClasses(emphasis: CellEmphasis = "normal", isLink?: boolean): string {
  const emphasisClasses = {
    strong: "font-semibold text-text-body",
    high: "font-semibold text-text-body",
    normal: "text-text-body",
    low: "text-text-muted",
  };

  return twMerge(emphasisClasses[emphasis], isLink && "hover:text-action-brand cursor-pointer transition-colors");
}

/**
 * Get container classes for cells
 * Includes min-w-0 and overflow-hidden to enable text truncation in flex containers
 */
export function getCellContainerClasses(align: CellAlignment = "left", additionalClasses?: string): string {
  return twMerge("flex items-center gap-1 min-w-0 overflow-hidden", getCellAlignmentClasses(align), additionalClasses);
}

/**
 * Format empty/null values consistently
 */
export function formatEmptyValue(value: unknown, emptyText: string = "—"): string {
  if (value === null || value === undefined || value === "") {
    return emptyText;
  }
  return String(value);
}

/**
 * Common loading skeleton sizes
 */
export const SKELETON_SIZES = {
  xs: "h-4 w-12",
  sm: "h-4 w-20",
  md: "h-4 w-24",
  lg: "h-4 w-32",
  xl: "h-4 w-40",
} as const;

/**
 * Get skeleton size based on emphasis level
 */
export function getSkeletonSize(emphasis: CellEmphasis = "normal"): string {
  const sizeMap = {
    strong: SKELETON_SIZES.lg,
    high: SKELETON_SIZES.lg,
    normal: SKELETON_SIZES.md,
    low: SKELETON_SIZES.sm,
  };
  return sizeMap[emphasis];
}

/**
 * Resolve a value that can be either static or dynamic (function)
 */
export function resolveValue<T, R>(value: R | ((row: T) => R), row: T): R {
  return typeof value === "function" ? (value as (row: T) => R)(row) : value;
}

/**
 * Common badge/chip styles for status indicators
 */
export function getBadgeClasses(
  variant: "success" | "error" | "warning" | "info" | "neutral" = "neutral",
  size: "sm" | "md" = "sm"
): string {
  const sizeClasses = size === "sm" ? "px-2 py-0.5 text-xs" : "px-2.5 py-1 text-sm";

  const variantClasses = {
    success: "bg-green-100 text-green-800",
    error: "bg-red-100 text-red-800",
    warning: "bg-yellow-100 text-yellow-800",
    info: "bg-blue-100 text-blue-800",
    neutral: "bg-gray-100 text-gray-800",
  };

  return twMerge("inline-flex items-center rounded-full font-medium", sizeClasses, variantClasses[variant]);
}

/**
 * Determine badge variant from boolean value
 */
export function getBooleanBadgeVariant(value: boolean): "success" | "neutral" {
  return value ? "success" : "neutral";
}

/**
 * Get color classes for numeric values
 */
export function getNumericColorClasses(value: number, colorCode: boolean = false): string {
  if (!colorCode) return "text-text-body";

  if (value > 0) return "text-green-600";
  if (value < 0) return "text-red-600";
  return "text-text-body";
}

/**
 * Get link styling classes based on behavior
 */
export function getLinkClasses(
  behavior: LinkBehavior = "hover",
  variant: "default" | "brand" | "muted" | "unstyled" = "default"
): string {
  // Variant color classes (matching TextLink)
  const variantClasses = {
    default: "text-action-primary hover:text-action-primary-hover visited:text-action-primary",
    brand: "text-action-brand hover:text-action-brand-hover visited:text-action-brand",
    muted: "text-text-muted hover:text-text-muted visited:text-text-muted",
    unstyled: "",
  };

  // Behavior classes (matching TextLink component)
  const behaviorClasses = {
    visible: "underline underline-offset-4 decoration-1 cursor-pointer",
    hover: "hover:underline hover:underline-offset-4 hover:decoration-1 cursor-pointer transition-colors",
    none: "cursor-pointer",
  };

  const colorClass = variantClasses[variant];
  const behaviorClass = behaviorClasses[behavior] || "";

  return `${colorClass} ${behaviorClass}`.trim();
}

/**
 * Wrap content with link if href is provided
 */
export function wrapWithLink<T>(
  content: ReactNode,
  href?: string | ((row: T) => string),
  row?: T,
  linkBehavior: LinkBehavior = "hover",
  linkVariant: "default" | "brand" | "muted" | "unstyled" = "default",
  emphasis?: CellEmphasis,
  className?: string
): ReactNode {
  if (!href) return content;

  const resolvedHref = typeof href === "function" && row ? href(row) : (href as string);
  const linkClasses = getLinkClasses(linkBehavior, linkVariant);

  // Add emphasis styling (bold for "strong") to the link
  const emphasisClass = emphasis === "strong" || emphasis === "high" ? "font-semibold" : "";

  return (
    <a
      href={resolvedHref}
      className={twMerge(linkClasses, emphasisClass, className)}
      onClick={(e) => {
        // Allow cmd/ctrl click for new tab
        if (e.metaKey || e.ctrlKey) return;
        // Otherwise, you could handle client-side routing here if needed
      }}
    >
      {content}
    </a>
  );
}
